import { useMutation } from '@tanstack/react-query'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBoxAndLinks, {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFiltersBody,
  VirtualCardsListContext,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import { translateNode } from 'src/localization/translateNode'
import { myWalletVirtualCardDetailUrl } from 'src/navigation/urls'
import { reportDownloadsMutations } from 'src/queries/reportDownloads/reportDownloads.queries'
import { createSetIsDownloadSuccessPopupOpen } from 'src/redux/actions/app.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectVirtualCardsActiveRecipientMeCountStatisticsFromState,
  selectVirtualCardsRecipientMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const MyWalletVirtualCards: React.FC = () => {
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const virtualCardsRecipientMeCount = useSelector(
    selectVirtualCardsRecipientMeCountStatisticsFromState,
  )
  const virtualCardsActiveRecipientMeCount = useSelector(
    selectVirtualCardsActiveRecipientMeCountStatisticsFromState,
  )
  const appName = useSelector(selectAppName)
  const user = useCurrentUser()
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)
  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()
  const reportVirtualCardsDownloadMutation = useMutation(
    reportDownloadsMutations.virtualCards(),
  )

  const [selectedFilters, setSelectedFilters] = useState<
    VirtualCardsAnalyticsEventProperties | undefined
  >(undefined)
  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)
  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 130)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 130)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.VirtualCardsRecipientMeCountStatistics,
      appStatisticsKeys.VirtualCardsActiveRecipientMeCountStatistics,
    ])
  }, [dispatchStatistics])

  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('my wallet virtual cards filter applied', {
        status: selectedFilters.status && selectedFilters.status.length > 0,
        search: !!selectedFilters.search,
        card_type:
          selectedFilters.card_type && selectedFilters.card_type.length > 0,
        deactivating_range: !!selectedFilters.deactivating_in,
      })
    }
  }, [analytics, selectedFilters])

  const dispatchPostReportVirtualCardsDownload = useCallback(
    (filters: VirtualCardsFiltersBody): void => {
      reportVirtualCardsDownloadMutation.mutate(filters, {
        onSuccess: () => {
          dispatch(createSetIsDownloadSuccessPopupOpen(true))
        },
      })
      analytics?.track('my wallet - virtual cards - download my virtual cards')
    },
    [analytics, dispatch, reportVirtualCardsDownloadMutation.mutate],
  )

  if (
    !statisticsLoaded.virtualCardsRecipientMeCountStatisticsLoaded ||
    !statisticsLoaded.virtualCardsActiveRecipientMeCountStatisticsLoaded
  ) {
    return <LoadingBackgroundPage />
  }

  return (
    <>
      <Helmet>
        <title>{`My Wallet Virtual Cards | ${appName}`}</title>
        <meta
          name="description"
          content={`My Wallet Virtual Cards page of ${appName}`}
        />
      </Helmet>
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <VirtualCardsBoxAndLinks
          context={VirtualCardsListContext.MyWallet}
          userId={user?.id}
          onDownloadClick={dispatchPostReportVirtualCardsDownload}
          downloadPending={reportVirtualCardsDownloadMutation.isPending}
          defaultTitle={
            <ObjectBoxTitle
              static={{
                label: translateNode('Active'),
                count: virtualCardsActiveRecipientMeCount,
              }}
            />
          }
          isYourCards
          emptyStateDesign={TableEmptyStateDesigns.YourVirtualCardsActive}
          totalCount={virtualCardsRecipientMeCount}
          setSelectedFilters={setSelectedFilters}
          clientHeight={clientHeight || 0}
          getVirtualCardDetailUrl={myWalletVirtualCardDetailUrl}
        />
      </div>
    </>
  )
}

export default MyWalletVirtualCards
